// src/components/SEO.js
import React from "react";
import { Helmet } from "react-helmet-async";

const ORIGIN = "https://modularobjects.com";
const toAbs = (u) => {
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  return `${ORIGIN}${u.startsWith("/") ? "" : "/"}${u}`;
};

// mapiranje jezika u og:locale
const localeByLang = (lang) => {
  const l = String(lang || "en").toLowerCase();
  if (l.startsWith("sr")) return "sr_RS";
  if (l.startsWith("de")) return "de_DE";
  if (l.startsWith("fr")) return "fr_FR";
  if (l.startsWith("es")) return "es_ES";
  if (l.startsWith("it")) return "it_IT";
  if (l.startsWith("nl")) return "nl_NL";
  if (l.startsWith("pl")) return "pl_PL";
  if (l.startsWith("pt")) return "pt_PT";
  if (l.startsWith("ro")) return "ro_RO";
  if (l.startsWith("ru")) return "ru_RU";
  if (l.startsWith("cs")) return "cs_CZ";
  if (l.startsWith("hu")) return "hu_HU";
  if (l.startsWith("zh")) return "zh_CN";
  return "en_US";
};

export default function SEO({
  title = "Modular Objects",
  description = "3D design and production workflow for custom furniture.",
  image = `${ORIGIN}/og-image.png`,
  url = "/",
  type = "website", // "website" | "article"
  lang = "en",
  noindex = false,

  // opcionalno
  keywords,
  canonical,
  alternates, // [{ lang:"sr", href:"/sr/blog/slug" }, { lang:"en", href:"/en/blog/slug" }]
}) {
  const absUrl = toAbs(canonical || url || "/");
  const absImg = toAbs(image);
  const ogLocale = localeByLang(lang);

  // fallback keywords ako ne proslediš
  const kw =
    keywords ||
    "custom kitchens, furniture, 3D design, CNC, interior planning, Modular Objects";

  return (
    <Helmet htmlAttributes={{ lang }}>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={kw} />

      {/* canonical */}
      <link rel="canonical" href={absUrl} />

      {/* alternates */}
      {Array.isArray(alternates) &&
        alternates.map((a) => (
          <link
            key={`${a.lang}-${a.href}`}
            rel="alternate"
            hrefLang={a.lang}
            href={toAbs(a.href)}
          />
        ))}

      {/* robots */}
      {noindex ? <meta name="robots" content="noindex,nofollow" /> : null}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={absUrl} />
      <meta property="og:image" content={absImg} />
      <meta property="og:locale" content={ogLocale} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absImg} />
    </Helmet>
  );
}